import {useEffect} from 'react';

/**
 * Custom hook for handling clicks outside a component. Please see the note
 * below on the dependency.
 *
 * @callback handlerCallback
 * @param  {object} ref - A node reference to check for event listeners
 * @param  {handlerCallback} handler - Callback for when event listener is triggered
 * @param  {object} [options] - `capture: true` beats capture-phase stopPropagation (freeze shield)
 *
 * @see {@link https://usehooks.com/useOnClickOutside/}
 */
function useOnClickOutside(ref, handler, {capture = false} = {}) {
  useEffect(
    () => {
      const listener = (event) => {
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener, {capture});
      document.addEventListener('touchstart', listener, {capture});

      return () => {
        document.removeEventListener('mousedown', listener, {capture});
        document.removeEventListener('touchstart', listener, {capture});
      };
    },
    /**
     * It's worth noting that because the passed in handler is
     * a new function on every render, it will cause this
     * effect's callback/cleanup to run every render. To optimize,
     * you can wrap the handler in useCallback before passing
     * it into this hook.
     */
    [ref, handler]
  );
}

export default useOnClickOutside;
